import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, DownloadSimple, Certificate as CertIcon, Link as LinkIcon } from "@phosphor-icons/react";

export default function Certificates() {
  const [certs, setCerts] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", cert_type: "TC", reason: "Annual transfer", details: "" });

  const refresh = () => api.get("/certificates").then((r) => setCerts(r.data));
  useEffect(() => {
    refresh();
    api.get("/students").then((r) => setStudents(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/certificates", form);
      toast.success(`Issued ${res.data.cert_no}`);
      setOpen(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const download = async (cert) => {
    try {
      const res = await api.get(`/certificates/${cert.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout title="Certificates" subtitle="Auto-numbered Transfer & Character certificates with PDF generation.">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-cert-btn" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
              <Plus size={14} className="mr-1.5" /> Issue certificate
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Issue certificate</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Student</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
                  <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.vs_id} — {s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Certificate type</Label>
                <Select value={form.cert_type} onValueChange={(v) => setForm({ ...form, cert_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TC">Transfer Certificate (TC)</SelectItem>
                    <SelectItem value="CC">Character Certificate (CC)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Reason</Label><Input required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
              <div><Label>Details (optional)</Label><Textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={3} /></div>
              <Button type="submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">
                <CertIcon size={14} className="mr-1.5" /> Generate PDF
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">Cert No.</th><th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Student</th><th className="px-4 py-3">Reason</th>
            <th className="px-4 py-3">Issued</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {certs.map((c) => (
              <tr key={c.id} data-testid={`cert-row-${c.cert_no.replace(/\//g, "-")}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono text-xs">{c.cert_no}</td>
                <td className="px-4 py-3 font-display font-semibold">{c.cert_type}</td>
                <td className="px-4 py-3">{c.student_name} <span className="text-xs text-[var(--vs-text-secondary)]">({c.student_vs_id})</span></td>
                <td className="px-4 py-3 text-xs">{c.reason}</td>
                <td className="px-4 py-3 font-mono text-xs">{new Date(c.issued_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 flex items-center gap-2">
                  <Button size="sm" variant="outline" data-testid={`download-cert-${c.cert_no.replace(/\//g, "-")}`} onClick={() => download(c)}>
                    <DownloadSimple size={12} className="mr-1.5" /> PDF
                  </Button>
                  <Link
                    to={`/verify/${encodeURIComponent(c.cert_no)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`verify-cert-${c.cert_no.replace(/\//g, "-")}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[var(--vs-border)] hover:bg-[var(--vs-bg-subtle)]"
                  >
                    <LinkIcon size={12} /> Verify link
                  </Link>
                </td>
              </tr>
            ))}
            {certs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--vs-text-secondary)]">No certificates issued yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
